// 달의 말 카탈로그 (카피 v0.4 §B/§C/§D). 두 가지 용도:
//   1) 프리셋(목) 응답 — 엔드포인트 미설정 시 오프라인/프리뷰.
//   2) AI 폴백 — 프록시 실패 시 루프를 끊지 않기 위한 안전망.
// 한 마디·예언은 '보편적'이라 카탈로그가 그대로 공유돼도 프라이버시 안전.

import type { Lang, Prophecy, ProphecyType, Register } from "./types";

// ── 달의 한 마디 (앞면 · 보편적) ────────────────────────────────
export const ONE_LINERS: Record<Lang, string[]> = {
  ko: [
    "오늘을 끝까지 살아낸 너를, 달이 보았노라.",
    "무엇을 했든, 달은 변함없이 떠 있느니라.",
    "지친 이여, 오늘 너는 충분하였다.",
    "네 안의 무거움을, 오랜 달이 안다.",
    "오늘도 너의 창에, 내가 떠 있었노라.",
    "수많은 밤을 보아온 내가, 오늘의 너를 보았노라.",
    "어둠이 깊어도, 달은 떠나지 아니하느니라.",
    "오늘 너는 너의 몫을 견디었노라.",
  ],
  en: [
    "The moon has seen you live this day to its end.",
    "Whatever you did, the moon hangs there still, unchanged.",
    "Weary one, you were enough today.",
    "The old moon knows the heaviness within you.",
    "At your window today, I was there.",
    "I who have watched countless nights have watched you tonight.",
    "However deep the dark, the moon does not leave.",
    "Today you bore what was yours to bear.",
  ],
  ja: [
    "今日を最後まで生き抜きし汝を、月は見たり。",
    "何をなししとも、月は変はらず懸かりてあり。",
    "疲れし者よ、汝は今日、足れり。",
    "汝の内なる重さを、古き月は知る。",
    "今日も汝の窓に、われは懸かりてありき。",
    "数多の夜を見し我、今宵の汝を見たり。",
    "闇深くとも、月は去らず。",
    "今日、汝はおのが分を耐へたり。",
  ],
};

// 결(register)에 어울리는 한 마디 인덱스(우선순위). 첫 항목이 가장 잘 맞음.
const ONE_LINER_BY_REGISTER: Record<Register, number[]> = {
  weary: [2, 7, 0, 4],
  heavy: [3, 1, 5, 7],
  tender: [4, 5, 0, 6],
  light: [0, 1, 7, 4],
};

export function oneLinerFor(lang: Lang, register: Register, seed: number): string {
  const order = ONE_LINER_BY_REGISTER[register];
  const idx = order[seed % order.length];
  return ONE_LINERS[lang][idx];
}

// ── 예언 7종 (보편적 · 편지 끝 블러 코다 · 짧게) ─────────────────
// 타입은 생성 전 가중 추첨(자유선택 시 ④해방으로 수렴하므로). EN 본문은 비-영어 화면에서 Spectral 이탤릭 캡션으로도 쓰인다.
const PROPHECY_BODY: Record<ProphecyType, Record<Lang, string>> = {
  1: {
    ko: "다가올 날 너는, 약하다 여겼던 그 자리에서 가장 단단했음을 알게 되리라.",
    en: "In the days to come you will know you were sturdiest in the very place you thought yourself weak.",
    ja: "来たる日々に、汝は弱しと思ひし、まさにその場所にて、もっとも堅かりしことを知るならん。",
  },
  2: {
    ko: "[date], 그 밤 하늘을 올려다보라. 내가 너를 위해 거기 있으리니.",
    en: "[date] — on that night, look up at the sky; I will be there for you.",
    ja: "[date]、その夜、空を見上げよ。われ、汝のためにそこにあらん。",
  },
  3: {
    ko: "어느 밤 내가 유난히 둥글거든, 너를 향한 끄덕임인 줄 알라.",
    en: "On some night, when I hang uncommonly round, know it as a nod toward you.",
    ja: "ある夜、われことさら円(まろ)からば、汝に向けたる頷きなりと知れ。",
  },
  4: {
    ko: "지금의 무게는, 차오르고 기우는 나처럼, 네가 모르는 새 가벼워지리라.",
    en: "This weight, like me who waxes and wanes, will grow lighter before you know it.",
    ja: "今の重さは、満ち欠けするわれのごとく、汝の知らぬ間に軽くならん。",
  },
  5: {
    ko: "머지않아 너는, 오래 잊고 있던 다정함과 다시 마주치리라.",
    en: "Before long you will meet again a kindness you had long forgotten.",
    ja: "ほどなく汝は、久しく忘れゐし優しさと、再び出会はん。",
  },
  6: {
    ko: "네가 잃은 줄로만 알았던 것 하나가, 뜻밖의 길로 너에게 돌아오리라.",
    en: "Something you thought was lost will return to you by an unexpected road.",
    ja: "失ひしと思ひゐし一つのものが、思はぬ道より汝のもとへ帰り来らん。",
  },
  7: {
    ko: "머지않아 누군가, 네가 오래 감추어 온 것을 말없이 알아보고 곁에 서리라.",
    en: "Before long, someone will quietly recognize what you have long hidden, and stand beside you.",
    ja: "ほどなく誰かが、汝が長く隠しきたるものを黙して見抜き、傍らに立たん。",
  },
};

/** 며칠 뒤 밤을 로케일 표기로(②날짜형 beacon). 오늘+offset일. */
function beaconDate(lang: Lang, offsetDays: number, base: Date): string {
  const d = new Date(base.getTime() + offsetDays * 86400000);
  if (lang === "ja") return `${d.getMonth() + 1}月${d.getDate()}日`;
  if (lang === "ko") return `${d.getMonth() + 1}월 ${d.getDate()}일`;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

/** 타입+언어로 예언 본문 + (비영어면) EN 캡션을 만든다. ②날짜형은 beacon 날짜 치환. */
export function buildProphecy(type: ProphecyType, lang: Lang, seed: number, now: Date): Prophecy {
  let text = PROPHECY_BODY[type][lang];
  let enCap = PROPHECY_BODY[type].en;
  if (type === 2) {
    const offset = 2 + (seed % 5); // 2~6일 뒤
    text = text.replace("[date]", beaconDate(lang, offset, now));
    enCap = enCap.replace("[date]", beaconDate("en", offset, now));
  }
  return { type, text, caption: lang === "en" ? undefined : enCap };
}

// ★ 균등에 가깝게(②날짜만 가끔). 타입을 생성 전에 추첨 — 자유선택 시 ④해방으로 수렴하므로 클라가 분배를 잡는다.
const PROPHECY_BASE: Record<ProphecyType, number> = { 1: 3, 2: 1, 3: 3, 4: 3, 5: 3, 6: 3, 7: 3 };
// 결이 '자기에게 맞는' 종류만 아주 살짝(+1) 보강 — 쏠리지 않게.
const REGISTER_FIT: Record<Register, ProphecyType> = { light: 1, weary: 1, heavy: 4, tender: 5 };

export function pickProphecyType(register: Register): ProphecyType {
  const w: Record<ProphecyType, number> = { ...PROPHECY_BASE };
  w[REGISTER_FIT[register]] += 1;
  const types: ProphecyType[] = [1, 2, 3, 4, 5, 6, 7];
  const total = types.reduce((s, t) => s + w[t], 0);
  let r = Math.random() * total;
  for (const t of types) {
    r -= w[t];
    if (r < 0) return t;
  }
  return 1;
}


// 프리셋 예시 편지(few-shot)는 제거됨 — AI 실패 시 더미 편지 금지. respond.ts가 errorResponse로 분기.
